import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { todayMexico, addDays, addBillingPeriod, periodStart, endOfMonth } from '@/lib/dates';

// Estatus con el que se generan las facturas recurrentes de forma automática.
// Cambia a 'draft' si prefieres que queden como borrador para revisión manual.
export const AUTO_INVOICE_STATUS = 'pending';

// Objeto de prorrateo encolado en la suscripción. Permite acumular múltiples
// adiciones de unidades hechas en fechas distintas y fusionarlas en la factura
// del ciclo correspondiente ("Expansión Alineada al Corte").
export interface PendingAddition {
  id: string;
  units: number;
  prepay_end_date: string;
  target_cut_date: string;
  prorated_subtotal: number;
  iva_amount: number;
  total_amount: number;
  description: string;
}

export interface RecurringBillingResult {
  created: number;
  skipped: number;
  errors: string[];
}

interface InvoiceItemInput {
  type: 'subscription' | 'proration' | 'product';
  ref_id: number;
  name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  iva: number;
  total: number;
}

let running = false;

// Candado de idempotencia: ¿existe ya una factura (EN CUALQUIER ESTATUS) para el
// cliente que cubra el mes indicado y que incluya un concepto de esta
// suscripción? Sirve para no volver a facturar un periodo ya emitido.
function hasInvoiceForMonth(
  invoices: { issue_date: string; items: any[] }[],
  subId: number,
  monthKey: string
): boolean {
  return invoices.some(
    (inv) =>
      (inv.issue_date || '').slice(0, 7) === monthKey
      && (inv.items || []).some((it: any) => it.type === 'subscription' && it.ref_id === subId)
  );
}

// Calcula la siguiente fecha de cobro aplicando la regla estricta de avance:
//  - Avanza EXACTAMENTE un ciclo (nunca dos hacia adelante de golpe).
//  - Candado de idempotencia: si el mes destino ya tiene una factura de esta
//    suscripción, NO se avanza (devuelve null).
// Devuelve la nueva fecha 'YYYY-MM-DD', o null si no corresponde avanzar.
export function computeNextBillingDate(
  sub: { id: number; next_billing_date: string; billing_cycle: string },
  clientInvoices: { issue_date: string; items: any[] }[]
): string | null {
  if (!sub.next_billing_date || !sub.billing_cycle) return null;
  const candidate = addBillingPeriod(sub.next_billing_date, sub.billing_cycle);
  if (!candidate) return null; // one_time u ciclo inválido
  const targetMonth = candidate.slice(0, 7);
  if (hasInvoiceForMonth(clientInvoices, sub.id, targetMonth)) return null;
  return candidate;
}

// Determina si un pago debe avanzar la suscripción. Solo se avanza cuando la
// factura pagada corresponde al PERIODO ACTUAL de la suscripción. Esto evita el
// doble avance: como el motor de fondo ya avanzó la fecha al emitir la factura,
// pagar esa misma factura NO vuelve a mover la fecha.
export function shouldAdvanceOnPayment(
  sub: { next_billing_date: string; billing_cycle: string },
  invoiceIssueDate: string
): boolean {
  if (!sub.next_billing_date || !invoiceIssueDate || !sub.billing_cycle) return false;
  const subPeriod = periodStart(sub.next_billing_date, sub.billing_cycle);
  const invoicePeriod = periodStart(invoiceIssueDate, sub.billing_cycle);
  return invoicePeriod === subPeriod;
}

// Normaliza el arreglo de prorrateos encolados de una suscripción.
export function getPendingAdditions(sub: { pending_additions?: unknown }): PendingAddition[] {
  const raw = sub?.pending_additions;
  if (!Array.isArray(raw)) return [];
  return raw.filter((it): it is PendingAddition => !!it && typeof it === 'object');
}

// Extrae los prorrateos encolados que corresponden a la fecha de corte que se
// está facturando: todos los objetos cuya `target_cut_date` sea menor o igual al
// día de corte del periodo facturado. Así, los prorrateos programados para este
// ciclo se fusionan como líneas de concepto individuales.
function dueAdditions(sub: any, cutDate: string): PendingAddition[] {
  const cut = cutDate || sub?.next_billing_date;
  if (!cut) return [];
  return getPendingAdditions(sub).filter(
    (a) => !!a.target_cut_date && a.target_cut_date <= cut
  );
}

// GENERADOR INTELIGENTE DE FACTURAS RECURRENTES
//
// Regla operativa central: la falta de pago de un mes previo NO impide facturar
// el mes corriente. Los servicios activos siguen devengando cobro mensual.
//
// Para cada suscripción activa, el motor CAMINA sus periodos por calendario
// (nunca por estatus de pago), empezando en su `next_billing_date`:
//  1. Si el periodo ya tiene factura emitida (en cualquier estatus) → avanza al
//     siguiente ciclo (AUTO-AVANCE) sin esperar a que se cobre.
//  2. Se detiene en el PRIMER periodo sin factura que ya sea exigible (es decir,
//     cuyo inicio caiga dentro del mes en curso o antes). Ese es el periodo a
//     facturar.
//  3. Emite una factura por cliente + periodo con la cantidad base, inyectando
//     los prorrateos en cola que correspondan al corte como líneas de concepto.
//  4. POST-FACTURACIÓN: avanza `next_billing_date` al siguiente ciclo (sin
//     depender del pago), suma las unidades de los prorrateos procesados a la
//     `quantity` base, recalcula montos y limpia la cola.
//
// Es idempotente: si un periodo ya fue facturado, se omite y solo se avanza la
// fecha. Así un cliente con facturas 'pending'/'overdue' de meses anteriores NO
// bloquea la emisión del mes corriente.
export async function runRecurringBilling(): Promise<RecurringBillingResult> {
  if (running) return { created: 0, skipped: 0, errors: [] };
  running = true;

  try {
    const res = await api.recurringBilling.run();
    if (res && res.result) {
      return res.result as RecurringBillingResult;
    }
    return { created: 0, skipped: 0, errors: [] };
  } catch (err: any) {
    return { created: 0, skipped: 0, errors: [err instanceof Error ? err.message : 'Error en la generación automática'] };
  } finally {
    running = false;
  }
}